import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeft, AlertCircle, Mail } from 'lucide-react';
import FestivalForm from '../components/FestivalForm';
import { useAuth } from '../contexts/AuthContext';

const FestivalEdit = () => {
  const { festivalId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  // Fetch festival data
  const { data: festivalData, isLoading, error } = useQuery(
    ['festival', festivalId],
    async () => {
      const response = await axios.get(`/api/festivals/${festivalId}`);
      return response.data;
    }
  );

  // Update festival mutation
  const updateFestivalMutation = useMutation(
    async (updatedData) => {
      const response = await axios.put(`/api/festivals/${festivalId}`, updatedData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Festival updated successfully!');
        queryClient.invalidateQueries(['festivals']);
        queryClient.invalidateQueries(['festival', festivalId]);
        navigate('/admin/festivals');
      },
      onError: (error) => {
        console.error('Update error:', error);
        if (error.response?.data?.requiresVerification) {
          setShowVerificationDialog(true);
        } else {
          toast.error(error.response?.data?.error || 'Failed to update festival');
        }
      }
    }
  );

  const handleSubmit = (formData) => {
    updateFestivalMutation.mutate(formData);
  };

  const handleCancel = () => {
    navigate('/admin/festivals');
  };

  // Prepare initial data for the form
  const getInitialData = () => {
    const festival = festivalData?.festival;
    if (!festival) return {};

    return {
      name: festival.name || '',
      description: festival.description || '',
      start_date: festival.start_date ? new Date(festival.start_date).toISOString().slice(0, 16) : '',
      end_date: festival.end_date ? new Date(festival.end_date).toISOString().slice(0, 16) : '',
      location_name: festival.location_name || '',
      location_lat: festival.location_lat,
      location_lng: festival.location_lng,
      zoom_level: festival.zoom_level || 15,
      status: festival.status || 'draft'
    };
  };

  if (isLoading) {
    return (
      <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
        <div>Loading festival...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error Loading Festival</h2>
        <p>{error.response?.data?.error || 'Failed to load festival'}</p>
        <button
          onClick={() => navigate('/admin/festivals')}
          className="btn btn-primary"
        >
          Back to Festivals
        </button>
      </div>
    );
  }

  const festival = festivalData?.festival;

  // Check if user can edit this festival
  if (festival && user && festival.creator_id !== user.id) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You can only edit festivals that you created.</p>
        <button
          onClick={() => navigate('/admin/festivals')}
          className="btn btn-primary"
        >
          Back to Festivals
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button
            onClick={() => navigate('/admin/festivals')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Festivals
          </button>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600' }}>
            Edit Festival: {festival?.name}
          </h1>
        </div>
      </div>

      {/* Edit Form */}
      <FestivalForm
        initialData={getInitialData()}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateFestivalMutation.isLoading}
        submitLabel="Update Festival"
        showStatus={true}
      />

      {/* Email Verification Required Dialog */}
      {showVerificationDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '450px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <AlertCircle size={60} style={{ color: '#f59e0b', marginBottom: '20px' }} />
            <h2 style={{ marginBottom: '15px', fontSize: '20px' }}>Email Verification Required</h2>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Please verify your email address before publishing festivals.
              You can request a new verification email from your Account page.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => navigate('/user/account')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <Mail size={16} />
                Go to Account
              </button>
              <button
                onClick={() => setShowVerificationDialog(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FestivalEdit;
